import { useTranslation } from "react-i18next";
import FormGroupTile from "../../../basic/FormGroupTile";
import AudienceMultiplierField from "../fields/AudienceMultiplierField";
import PlacementModifierWarning from "./PlacementModifierWarning";
import {
  ALGORITHM_OPTIONS,
  AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES,
  AMAZON_BUSINESS_SITE_RESTRICTION,
} from "../constants";
import { useAppSelector } from "../../../../../redux/hooks";
import { css } from "../../../../../../styled-system/css";
import { Flex } from "../../../../../../styled-system/jsx";
import { Checkbox } from "../../../../fields/base/Checkbox";

const subtitleStyle = css({
  fontWeight: 560,
  fontSize: "1.6rem",
  color: "#73706E",
});

const explanationTextStyle = css.raw({
  margin: "small 0",
  color: "#6C757D",
  fontSize: "1.3rem",
  fontWeight: 500,
  maxWidth: "90%",
});

const reviewExplanationTextStyle = css.raw({
  maxWidth: "100%",
});

const SponsoredProductCampaignModifiers = ({
  state,
  onChange,
  errors,
  fieldsToValidate,
  review,
  bidStrategyOptions,
  readOnly,
}) => {
  const { t } = useTranslation(["advertising"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  // Amazon rejects a campaign that carries both a site restriction and a B2B bid
  // adjustment, and the restriction already confines delivery to Amazon Business.
  const restrictedToAmazonBusiness = state.siteRestrictions === AMAZON_BUSINESS_SITE_RESTRICTION;

  const disablePlacementModifiers =
    state.algorithm === ALGORITHM_OPTIONS.PLACEMENT_MAX.value ||
    ([ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(state.algorithm) &&
      state.algoManagedPlacementMultipliers);

  return (
    <Flex
      wrap="wrap"
      style={{
        marginBottom: "0",
      }}
    >
      <div style={{ width: "100%" }}>
        <h3 className={subtitleStyle}>{t(($) => $.advertising["Amazon Options:"])}</h3>
      </div>
      {[ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(state.algorithm) ?
        <>
          <div
            className={css({
              width: "100%",
              lg: { width: "41.67%" },
              padding: "small",
            })}
          >
            <Checkbox
              label={t(($) => $.advertising["Algorithmically Manage Placement Multipliers"])}
              value={state.algoManagedPlacementMultipliers}
              isDisabled={readOnly}
              onChange={(v) => {
                onChange({
                  algoManagedPlacementMultipliers: v,
                });
              }}
            />
          </div>
          <div className={css({ width: "100%", lg: { width: "58.33%" }, paddingTop: "medium" })}>
            <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
              {t(
                ($) =>
                  $.advertising[
                    "Select this option to enable Placement Boost and let Trellis automatically optimize your placement multipliers."
                  ],
              )}
            </p>
          </div>
        </>
      : null}
      <Flex
        wrap="wrap"
        className={css({
          width: "100%",
          lg: { width: "41.67%" },
          padding: "0 small small 0",
        })}
        style={{
          paddingTop: "8px",
        }}
      >
        <div className={css({ width: "100%", "2xl": { width: "50%" } })} style={{ padding: "0 2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            {t(($) => $.advertising["Bid Strategy"])}
          </label>
          <FormGroupTile
            type="select"
            stateName={"bidStrategy"}
            handleChildFormElement={(key, value) => onChange({ [key]: value.value })}
            disabled={readOnly}
            defaultValue={bidStrategyOptions.find((strategy) => strategy.value === state.bidStrategy)}
            options={bidStrategyOptions}
          />
        </div>
        <div className={css({ width: "100%", "2xl": { width: "50%" } })} style={{ padding: "0 2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            {t(($) => $.advertising["Top of Search"])}
          </label>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            prefix={"%"}
            prefixSide={"right"}
            stateName={"topOfSearchBidMultiplier"}
            handleChildFormElement={(key, value) => onChange({ [key]: value })}
            defaultValue={state.topOfSearchBidMultiplier}
            disabled={disablePlacementModifiers || readOnly}
          />
        </div>

        <div className={css({ width: "100%", "2xl": { width: "50%" } })} style={{ padding: "2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            {t(($) => $.advertising["Product Pages"])}
          </label>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            prefix={"%"}
            prefixSide={"right"}
            stateName={"productPageMultiplier"}
            handleChildFormElement={(key, value) => {
              onChange({ [key]: value });
            }}
            defaultValue={state.productPageMultiplier}
            disabled={disablePlacementModifiers || readOnly}
          />
        </div>
        <div className={css({ width: "100%", "2xl": { width: "50%" } })} style={{ padding: "2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            {t(($) => $.advertising["Rest of the Search"])}
          </label>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            prefix={"%"}
            prefixSide={"right"}
            stateName={"restOfSearchMultiplier"}
            handleChildFormElement={(key, value) => {
              onChange({ [key]: value });
            }}
            defaultValue={state.restOfSearchMultiplier}
            disabled={disablePlacementModifiers || readOnly}
          />
        </div>
        {AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace) && !restrictedToAmazonBusiness ?
          <div className={css({ width: "100%", "2xl": { width: "50%" } })} style={{ padding: "0 2rem" }}>
            <label
              style={{
                color: "#73706E",
                fontWeight: "600",
                paddingBottom: ".7rem",
              }}
            >
              {t(($) => $.advertising["Amazon Business (All Placements)"])}
            </label>
            <FormGroupTile
              type="formcontrol"
              formControlType={"number"}
              prefix={"%"}
              prefixSide={"right"}
              stateName={"amazonBusinessMultiplier"}
              handleChildFormElement={(key, value) => {
                onChange({ [key]: value });
              }}
              defaultValue={state.amazonBusinessMultiplier}
              disabled={readOnly}
            />
          </div>
        : null}
        <div style={{ padding: "0 2rem" }}>
          {errors.topOfSearchBidMultiplier && fieldsToValidate?.includes("topOfSearchBidMultiplier") ?
            <small className="text-danger" style={{ paddingTop: "1rem" }}>
              {errors.topOfSearchBidMultiplier}
            </small>
          : errors.productPageMultiplier && fieldsToValidate?.includes("productPageMultiplier") ?
            <small className="text-danger" style={{ paddingTop: "1rem" }}>
              {errors.productPageMultiplier}
            </small>
          : errors.amazonBusinessMultiplier && fieldsToValidate?.includes("amazonBusinessMultiplier") ?
            <small className="text-danger" style={{ paddingTop: "1rem" }}>
              {errors.amazonBusinessMultiplier}
            </small>
          : errors.restOfSearchMultiplier &&
            fieldsToValidate?.includes("restOfSearchMultiplier") && (
              <small className="text-danger" style={{ paddingTop: "1rem" }}>
                {errors.restOfSearchMultiplier}
              </small>
            )
          }
        </div>
      </Flex>
      <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
        <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)} style={{ paddingTop: "2rem" }}>
          {t(
            ($) =>
              $.advertising[
                "Let Amazon automatically bid more when doing so will bump your product to a relevant product page's top spot."
              ],
          )}
        </p>
        <PlacementModifierWarning state={state} review={review} />
      </div>

      <div className={css({ width: "100%", lg: { width: "41.67%" } })}>
        <AudienceMultiplierField
          state={state}
          onChange={onChange}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          isMediaPlanField={true}
          disableFields={readOnly}
        />
      </div>
      <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
        <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)} style={{ paddingTop: "2rem" }}>
          {t(($) => $.advertising["Apply a bid multiplier when a shopper matches a target audience."])}
        </p>
        <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)} style={{ paddingTop: "1rem" }}>
          {t(
            ($) =>
              $.advertising[
                "Not seeing your AMC Audience? AMC Audiences can take up to 48 hours to be available in advertising."
              ],
          )}
        </p>
      </div>
    </Flex>
  );
};
export default SponsoredProductCampaignModifiers;
